import type { TFunction } from "i18next";
import type { DayConflict } from "@/services/officialDayConflictService";
import type { DisplayMode } from "@/utils/nameHelpers";
import { getLocalizedLeagueName, getLocalizedStadiumName, getTeamDisplayName } from "@/utils/teamNameDisplay";
import { DEFAULT_ROLE_TRANSLATIONS } from "@/types/league-roles";

/** One line per conflicting designation, single-language by displayMode (planning namespace keys). */
export function formatDayConflictLines(conflicts: DayConflict[], mode: DisplayMode, t: TFunction): string[] {
  return conflicts.map((c) => {
    const role = DEFAULT_ROLE_TRANSLATIONS[c.role];
    return t("day_conflict.detail_line", {
      league: getLocalizedLeagueName({ name: c.leagueName, name_ar: c.leagueNameAr }, mode),
      home: getTeamDisplayName({ name: c.homeTeamName, name_ar: c.homeTeamNameAr }, mode),
      away: getTeamDisplayName({ name: c.awayTeamName, name_ar: c.awayTeamNameAr }, mode),
      time: c.matchTime ? c.matchTime.slice(0, 5) : t("day_conflict.time_unknown"),
      stadium: c.stadiumName
        ? getLocalizedStadiumName({ name: c.stadiumName, name_ar: c.stadiumNameAr }, mode)
        : t("day_conflict.stadium_unknown"),
      role: role ? (mode === "arabic" ? role.nameAr : role.nameFr) : c.role,
    });
  });
}

/**
 * True when `error` (a PostgREST error, an Error, or an Error wrapping it in
 * `originalError` as utils/errorUtils.logAndThrow does) carries the SQL code `code`
 * in its message.
 */
export function errorMessageIncludes(error: unknown, code: string): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 3 && current && typeof current === "object"; depth++) {
    const message = (current as { message?: unknown }).message;
    if (typeof message === "string" && message.includes(code)) return true;
    current = (current as { originalError?: unknown }).originalError;
  }
  return false;
}

/** The designation was refused because the official is not an active member of the match's league. */
export function isNotLeagueMemberError(error: unknown): boolean {
  return errorMessageIncludes(error, "OFFICIAL_NOT_LEAGUE_MEMBER");
}
